import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { hoaHopTui, mucGiaConfig, getFlowerImage, formatGia } from "./flowerData";
import bannerHopTui from "./assets/hoa_hop_tui_banner.png";
import { ToastNotification, useToast } from "./ToastNotification";
import FlowerModal from "./FlowerModal";

const MUC_ORDER = ["duoi-250k", "250k-500k", "500k-1000k", "1000k-2000k", "tren-2000k"];

export default function HoaHopTui() {
  const navigate = useNavigate();
  const { muc } = useParams();
  const activeMuc = MUC_ORDER.includes(muc) ? muc : "duoi-250k";
  const config = mucGiaConfig[activeMuc];
  const { toast, setToast, showToast } = useToast();
  const [hoveredId, setHoveredId] = useState(null);
  const [sort, setSort] = useState("asc");
  const [selectedFlower, setSelectedFlower] = useState(null);

  const danhSach = hoaHopTui
    .filter((h) => h.muc === activeMuc)
    .sort((a, b) => sort === "asc" ? a.gia - b.gia : b.gia - a.gia);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeMuc]);

  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Pinyon+Script&family=Montserrat:wght@200;300;400;500&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  const themGioHang = (hoa, e) => {
    e.stopPropagation();
    const cart = JSON.parse(localStorage.getItem("sellixy_cart") || "[]");
    const idx = cart.findIndex((i) => i.id === hoa.id);
    if (idx >= 0) {
      cart[idx].quantity = (cart[idx].quantity || 1) + 1;
    } else {
      cart.push({ id: hoa.id, name: hoa.ten, price: hoa.gia, img: getFlowerImage(hoa.ma), ma: hoa.ma, quantity: 1 });
    }
    localStorage.setItem("sellixy_cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));
    showToast(`Đã thêm "${hoa.ten}" vào giỏ hàng!`, "success");
  };

  return (
    <div style={s.page}>
      <ToastNotification toast={toast} setToast={setToast} />
      <FlowerModal
        flower={selectedFlower}
        config={selectedFlower ? mucGiaConfig[selectedFlower.muc] : null}
        onClose={() => setSelectedFlower(null)}
      />

      {/* BANNER */}
      <div style={s.bannerWrap}>
        <img src={bannerHopTui} alt="Hoa Hợp Túi" style={s.bannerImg} />
      </div>

      {/* FILTER TABS */}
      <div style={s.stickyBar}>
        <div style={s.tabsWrap}>
          {MUC_ORDER.map((key) => {
            const c = mucGiaConfig[key];
            const isActive = key === activeMuc;
            return (
              <button
                key={key}
                onClick={() => navigate(`/hop-tui/${key}`)}
                style={{
                  ...s.tabBtn,
                  color: isActive ? "#111111" : "#888888",
                  borderBottom: isActive ? "2px solid #4a5a3a" : "2px solid transparent",
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* PRODUCTS SECTION */}
      <section style={s.section}>
        <div style={s.sectionHeader}>
          <span style={s.editorialSub}>Bộ sưu tập hợp túi</span>
          <h2 style={s.sectionTitle}>{config.label}</h2>
          <div style={s.titleDivider} />
          <div style={s.sortRow}>
            <p style={s.productCount}>{danhSach.length} tác phẩm nghệ thuật</p>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              style={s.sortSelect}
            >
              <option value="asc">Giá tăng dần</option>
              <option value="desc">Giá giảm dần</option>
            </select>
          </div>
        </div>

        {danhSach.length === 0 ? (
          <div style={s.emptyBox}>
            <p style={s.emptyText}>Chưa có sản phẩm trong mức giá này.</p>
          </div>
        ) : (
          <motion.div
            key={activeMuc + sort}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            style={s.grid}
          >
            {danhSach.map((hoa, i) => (
              <motion.div
                key={hoa.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: (i % 4) * 0.15 }}
                style={s.card}
                onMouseEnter={() => setHoveredId(hoa.id)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => setSelectedFlower(hoa)}
              >
                <div style={s.imgWrap}>
                  <motion.img
                    animate={{ scale: hoveredId === hoa.id ? 1.08 : 1 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    src={getFlowerImage(hoa.ma)}
                    alt={hoa.ten}
                    style={s.img}
                  />
                  <div style={s.badge}>{config.label}</div>

                  <AnimatePresence>
                    {hoveredId === hoa.id && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        style={s.hoverOverlay}
                      >
                        <motion.div
                          initial={{ y: 20 }}
                          animate={{ y: 0 }}
                          exit={{ y: 10 }}
                          style={{ textAlign: "center" }}
                        >
                          <h4 style={s.hoverName}>{hoa.ten}</h4>
                          <p style={s.hoverPrice}>{formatGia(hoa.gia)}</p>
                          <p style={s.hoverCta}>Xem chi tiết ✧</p>
                          <button
                            style={s.hoverCartBtn}
                            onClick={(e) => themGioHang(hoa, e)}
                          >
                            + Thêm vào giỏ
                          </button>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </section>
    </div>
  );
}

const s = {
  page: {
    fontFamily: '"Cormorant Garamond", serif',
    backgroundColor: "#ffffff",
    color: "#111111",
    minHeight: "100vh",
    overflowX: "hidden",
  },
  bannerWrap: { width: "100%", overflow: "hidden" },
  bannerImg: { width: "100%", height: "auto", display: "block" },
  stickyBar: {
    position: "sticky",
    top: 70,
    zIndex: 100,
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    borderBottom: "1px solid #eeeeee",
  },
  tabsWrap: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "30px",
    padding: "14px 32px",
  },
  tabBtn: {
    background: "none",
    border: "none",
    borderTop: "none",
    borderLeft: "none",
    borderRight: "none",
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "11px",
    letterSpacing: "2px",
    textTransform: "uppercase",
    cursor: "pointer",
    padding: "8px 4px",
    transition: "all 0.3s ease",
    fontWeight: 400,
    whiteSpace: "nowrap",
  },
  section: { padding: "80px 6%", backgroundColor: "#ffffff" },
  sectionHeader: { textAlign: "center", marginBottom: "60px" },
  editorialSub: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "11px",
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: "4px",
    color: "#777777",
    display: "block",
    marginBottom: "14px",
  },
  sectionTitle: {
    fontFamily: '"Cormorant Garamond", serif',
    fontSize: "clamp(26px, 3.5vw, 36px)",
    fontWeight: 400,
    letterSpacing: "3px",
    textTransform: "uppercase",
    color: "#1a1a1a",
    margin: "0 0 20px",
  },
  titleDivider: {
    width: "50px",
    height: "1px",
    backgroundColor: "#4a5a3a",
    margin: "0 auto 20px",
  },
  sortRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "20px",
    flexWrap: "wrap",
  },
  productCount: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "11px",
    color: "#999999",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    margin: 0,
  },
  sortSelect: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "11px",
    letterSpacing: "1px",
    textTransform: "uppercase",
    padding: "6px 14px",
    border: "1px solid #cccccc",
    background: "#ffffff",
    color: "#444444",
    cursor: "pointer",
    outline: "none",
  },
  emptyBox: { textAlign: "center", padding: "80px 0" },
  emptyText: {
    fontFamily: '"Montserrat", sans-serif',
    color: "#999",
    fontSize: "13px",
    letterSpacing: "1px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: "15px 2px",
  },
  card: {
    position: "relative",
    cursor: "pointer",
    overflow: "hidden",
  },
  imgWrap: {
    position: "relative",
    width: "100%",
    aspectRatio: "3/4",
    overflow: "hidden",
    backgroundColor: "#000000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  img: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    padding: "25px",
  },
  badge: {
    position: "absolute",
    top: "15px",
    left: "15px",
    backgroundColor: "rgba(255,255,255,0.9)",
    color: "#111111",
    padding: "6px 12px",
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "9px",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    zIndex: 2,
  },
  hoverOverlay: {
    position: "absolute",
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(10,10,10,0.75)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    zIndex: 3,
    backdropFilter: "blur(5px)",
    WebkitBackdropFilter: "blur(5px)",
  },
  hoverName: {
    fontFamily: '"Cormorant Garamond", serif',
    color: "#ffffff",
    fontSize: "22px",
    fontWeight: 400,
    letterSpacing: "1px",
    margin: "0 0 12px",
  },
  hoverPrice: {
    color: "#d4af37",
    fontSize: "15px",
    fontFamily: '"Montserrat", sans-serif',
    margin: "0 0 16px",
    fontWeight: 500,
  },
  hoverCta: {
    color: "#ffffff",
    fontSize: "10px",
    textTransform: "uppercase",
    letterSpacing: "2px",
    borderBottom: "1px solid #d4af37",
    paddingBottom: "4px",
    margin: "0 0 20px",
    fontFamily: '"Montserrat", sans-serif',
  },
  hoverCartBtn: {
    backgroundColor: "transparent",
    border: "1px solid rgba(255,255,255,0.6)",
    color: "#ffffff",
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "10px",
    textTransform: "uppercase",
    letterSpacing: "2px",
    padding: "10px 24px",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
};
